import { useMemo, useRef, useState } from 'react';

import {
  useApprovalFlowStatuses,
  type UseApprovalFlowStatusesOptions,
} from '@mint-lib/approval-flow-circuit_v2';
import { useTranslation } from '@mint-lib/i18n';
import { Delete, Idea } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  Card,
  Divider,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import { usePreviewActivityListApi } from '../../../../../../api/queries/usePreviewActivityListApi.js';
import { useSidebarTreeApi } from '../../../../../../api/queries/useSidebarTreeApi.js';
import { MP_LEVELS } from '../../../../../../consts/index.js';
import { useChosenLevel } from '../../../../../../hooks/useChosenLevel.js';
import { useFlights } from '../../../../../../hooks/useFlights.js';
import BudgetInfo from './BudgetInfo.jsx';
import FlightsTable from './FlightsTable.jsx';
import NewFlight from './NewFlight.jsx';
import NoFlights from './NoFlights.jsx';

const isVisible = false;

// soft delete for this section, it is going to be reworked but maybe some code will be useful
const isAvailableBudgetSectionVisible = false;

const FlightsDrawerContent = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation('@myn/planning_v2');

  const { id, level } = useChosenLevel();
  const { data: sidebarTree } = useSidebarTreeApi();
  const [open, setOpen] = useState<boolean>(false);

  const { data: activityList, isLoading: isActivityListLoading } =
    usePreviewActivityListApi();

  const [userSelectedActivity, setUserSelectedActivity] = useState<
    string | null
  >(null);

  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const ff = useFlags('@mint/arm', ['newApprovalMicro']);
  const isApprovalFlowEnabled = ff.newApprovalMicro.enabled;

  const derivedActivityValue = useMemo(() => {
    if (!id || !sidebarTree) {
      return '';
    }

    if (level === MP_LEVELS.FLIGHT) {
      return findActivityIdForFlight(id, sidebarTree);
    }

    if (level === MP_LEVELS.ACTIVITY) {
      return findActivityIdForActivity(id, sidebarTree);
    }

    return '';
  }, [id, sidebarTree, level]);

  const chosenActivityValue = userSelectedActivity ?? derivedActivityValue;

  const currentActivityList = useMemo(
    () =>
      activityList?.items.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [activityList?.items],
  );

  const chosenActivity = useMemo(
    () =>
      currentActivityList?.find((item) => item.value === chosenActivityValue) ??
      currentActivityList?.at(0) ?? { label: '', value: '' },
    [currentActivityList, chosenActivityValue],
  );

  const flightsGridRef = useRef(null);

  const {
    data: flightsData,
    isLoading: isFlightsLoading,
    handleCreateFlights,
    handleCellUpdate,
    handleDateCellUpdate,
    handleDeleteLastFlights,
  } = useFlights({
    flightsGridRef,
    activityId: chosenActivity.value,
  });

  const entityList = flightsData?.items.map((item) => {
    return {
      entityType: 'flight',
      entitySource: 'planning-service',
      entityId: item.id,
    } satisfies UseApprovalFlowStatusesOptions['entities'][number];
  });

  const {
    isPending: isApprovalStatusesPending,
    isError: isApprovalStatusesError,
    data: approvalStatusesData,
  } = useApprovalFlowStatuses({
    entities: entityList ?? [],
    enabled: isApprovalFlowEnabled,
  });

  const lockedFlightIds = useMemo(() => {
    if (!isApprovalFlowEnabled) {
      return new Set<string>();
    }

    return new Set(
      approvalStatusesData?.reduce<string[]>((acc, item) => {
        if (!item.isLocked) {
          return acc;
        }

        acc.push(item.entityId);
        return acc;
      }, []),
    );
  }, [isApprovalFlowEnabled, approvalStatusesData]);

  const isDataLoading =
    isActivityListLoading ||
    isFlightsLoading ||
    (isApprovalFlowEnabled &&
      (isApprovalStatusesPending || isApprovalStatusesError));

  const lastFlightId = flightsData?.items.at(-1)?.id;
  const isLastFlightLocked =
    !!lastFlightId && lockedFlightIds.has(lastFlightId);

  return (
    <>
      <div className="mb-4">
        {isActivityListLoading ? (
          <Skeleton className="h-full" aria-label="skeleton" />
        ) : (
          <ComboboxInputRoot>
            <ComboboxInputLabel
              label={t('Activity')}
              htmlFor="manage-flights-activity-select"
            />
            <ComboboxInput
              id="manage-flights-activity-select"
              popoverClassName="z-popover"
              value={chosenActivity.label}
              placeholder={chosenActivity.label}
              open={open}
              onOpenChange={setOpen}
            >
              <ComboboxMenu>
                {currentActivityList.map((option) => (
                  <ComboboxItem
                    key={option.value}
                    value={option.value}
                    onSelect={(value) => {
                      setUserSelectedActivity(value);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </ComboboxItem>
                ))}
              </ComboboxMenu>
            </ComboboxInput>
          </ComboboxInputRoot>
        )}
      </div>
      {isAvailableBudgetSectionVisible && (
        <div className="mb-4">
          <p
            className={cn(
              typographyVariants({ variant: 'label01', color: 'secondary' }),
              'mb-4',
            )}
          >
            {t('Available budget')}
          </p>
          <div className="flex gap-4">
            <BudgetInfo
              level="mediaPlan"
              budgetInfo={{
                total: 200,
                planned: 100,
                unplanned: 100,
                startDate: '2023-01-01',
                endDate: '2023-12-31',
              }}
              name="Media Plan 1"
            />
            <div className="flex">
              <Divider orientation="vertical" className="mr-4 ml-4" />
              <BudgetInfo
                level="activity"
                name="Activity 1"
                budgetInfo={{
                  total: 200,
                  planned: 100,
                  unplanned: 100,
                  startDate: '2023-01-01',
                  endDate: '2023-12-31',
                }}
              />
            </div>
          </div>
        </div>
      )}
      <div>
        <Card contentClassName="flex justify-between p-2" className="w-full">
          <div className="flex gap-2">
            <NewFlight
              additional={flightsData?.additional}
              handleCreateFlights={handleCreateFlights}
            />
            {isVisible && (
              <Button
                variant="outlined"
                size="medium"
                aria-label="Distribute budget"
                color="primary"
                disabled={isReadOnly}
              >
                <Idea />
                {t('Distribute budget')}
              </Button>
            )}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteLastFlights}
                  aria-label="Delete last flight"
                  disabled={
                    isReadOnly ||
                    !flightsData?.additional?.isLastFlightDeletable ||
                    isLastFlightLocked
                  }
                  size="medium"
                >
                  <Delete />
                  {t('Delete last flight')}
                </Button>
              </span>
            </TooltipTrigger>
            {isLastFlightLocked && (
              <TooltipContent className="z-tooltip">
                {t('Flight is pending approval. Deletion is not allowed.')}
              </TooltipContent>
            )}
          </Tooltip>
        </Card>
      </div>
      <div className="mt-4 h-full">
        {flightsData?.items.length || isDataLoading ? (
          <FlightsTable
            flightsGridRef={flightsGridRef}
            flightsData={flightsData}
            handleDateCellUpdate={handleDateCellUpdate}
            handleCellUpdate={handleCellUpdate}
            isLoading={isDataLoading}
            lockedFlightIds={lockedFlightIds}
          />
        ) : (
          <NoFlights />
        )}
      </div>
    </>
  );
};

export default FlightsDrawerContent;

function findActivityIdForActivity(
  activityMediaDataId: string,
  sidebarTree: MediaPlan.SidebarItem,
) {
  const foundActivity = sidebarTree.subItems.find(
    (activity) => activity.mediaDataId === activityMediaDataId,
  );

  return foundActivity?.activityId ?? '';
}

function findActivityIdForFlight(
  flightMediaDataId: string,
  sidebarTree: MediaPlan.SidebarItem,
) {
  const foundActivity = sidebarTree.subItems.find((activity) =>
    activity.subItems.some(
      (flight) => flight.mediaDataId === flightMediaDataId,
    ),
  );

  return foundActivity?.activityId ?? '';
}
